import React, { useRef, useState, useEffect } from "react";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { Column, ColumnEditorOptions } from "primereact/column";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import desiriBullService from "../../services/desiriBull/bullservice";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";

interface Props {
  data: any[];
  onRefresh: () => void;
}

const DesiriBullsTable: React.FC<Props> = ({ data, onRefresh }) => {
  const toast = useRef<Toast>(null);
  const [bullData, setBullData] = useState<any[]>([]);
  const [MUOptions, setMUOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [pairBullOptions, setPairBullOptions] = useState<
    { label: string; value: number }[]
  >([]);
  useEffect(() => {
    setBullData([...data].sort((a, b) => b.id - a.id));
  }, [data]);
  useEffect(() => {
    const fetchMUPlants = async () => {
      try {
        const response = await desiriBullService.getAllMUPlants();
        const formatted = response.map((d: any) => ({
          label: d.posName,
          value: d.posID,
        }));
        setMUOptions(formatted);
      } catch (error) {
        console.error("Failed to load Manufacture Plants:", error);
      }
    };

    fetchMUPlants();
  }, []);
  const textEditor = (options: ColumnEditorOptions) => {
    return (
      <InputText
        type="text"
        value={options.value}
        style={{ width: "90%", border: "1px solid #090" }}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          options.editorCallback!(e.target.value)
        }
      />
    );
  };
  const confirmDelete = (rowData: any) => {
    confirmDialog({
      message: `Are you sure you want to delete "${rowData.name}"?`,
      header: "Confirm Deletion",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => handleDelete(rowData),
    });
  };

  const handleDelete = async (rowData: any) => {
    try {
      const res = await desiriBullService.deleteBull(rowData.id);
      if (res.isSuccess) {
        setBullData((prev) => prev.filter((b) => b.id !== rowData.id));
        toast.current?.show({
          severity: "success",
          summary: "Delete Bull",
          detail: res.message,
          life: 3000,
        });
        onRefresh();
      } else {
        toast.current?.show({
          severity: "warn",
          summary: "Delete Bull",
          detail: res.message || "Failed to delete bull.",
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Error deleting bull:", error);
      toast.current?.show({
        severity: "error",
        summary: "Delete Bull",
        detail: "An error occurred while deleting the bull.",
        life: 3000,
      });
    }
  };

const onRowEditComplete = async (event: DataTableRowEditCompleteEvent) => {
  const { newData, index } = event;

  const errors: string[] = [];
  if (!newData.name?.trim()) errors.push("Name is required");
  if (!newData.bullNumber) errors.push("Bull Number is required");
  if (!newData.dob) errors.push("DOB is required");
  if (!newData.weightKG) errors.push("Weight is required");

  if (errors.length > 0) {
    toast.current?.show({
      severity: "warn",
      summary: "Validation Error",
      detail: errors.join(", "),
      life: 4000,
    });
    return;
  }
  debugger
  const payload = {
    id: newData.id ?? bullData[index].id,
    posID: newData.posID ?? 0,
    bullNumber: Number(newData.bullNumber),
    name: newData.name?.trim(),
    dob: newData.dob ? new Date(newData.dob).toISOString() : null,
    age: Number(newData.age) || 0,
    gender: newData.gender || "",
    breed: newData.breed || "",
    color: newData.color || "",
    weightKG: Number(newData.weightKG) || 0,
    isForBreeding: Boolean(newData.isForBreeding),
    pairOfTheBull: newData.pairOFthebull ?? 0 
  };

  try {
    const res = await desiriBullService.updateBull(payload);
    if (res.isSuccess) {
      const updatedData = [...bullData];
      updatedData[index] = { ...bullData[index], ...newData };
      setBullData(updatedData);
      toast.current?.show({
        severity: "success",
        summary: "Update Bull",
        detail: res.message,
        life: 3000,
      });
      onRefresh();
    } else {
      toast.current?.show({
        severity: "warn",
        summary: "Update Bull",
        detail: res.message || "Failed to update bull.",
        life: 3000,
      });
    }
  } catch (error) {
    console.error("Error updating bull:", error);
    toast.current?.show({
      severity: "error",
      summary: "Update Bull",
      detail: "An error occurred while updating the bull.",
      life: 3000,
    });
  }
};

  const muEditor = (options: any) => {
    return (
      <Dropdown
        value={options.value} 
        options={MUOptions} 
        onChange={(e) => options.editorCallback(e.value)}
        placeholder="Select Manufacturing Unit"
        filter 
        showClear
        className="w-full"
      />
    );
  };
  const dobEditor = (options: any) => {
    return (
      <Calendar
        value={options.value ? new Date(options.value) : null}
        onChange={(e) => {
          if (e.value instanceof Date) {
            const isoDate = e.value.toLocaleDateString("en-CA"); 
            options.editorCallback(isoDate);
          } else {
            options.editorCallback(null);
          }
        }}
        dateFormat="yy-mm-dd"
        className="w-full"
      />
    );
  };
  const isForBreedingOptions = [
    { label: "Yes", value: true },
    { label: "No", value: false },
  ];

  const isForBreedingEditor = (options: any) => (
    <Dropdown
      value={options.value}
      options={isForBreedingOptions}
      onChange={(e) => options.editorCallback(e.value)}
      placeholder="Select Breeding Status"
      className="w-full"
    />
  );
  const genderOptions = [
    { label: "Male", value: "Male" },
    { label: "Female", value: "Female" },
  ];

  const genderEditor = (options: any) => (
    <Dropdown
      value={options.value}
      options={genderOptions}
      onChange={(e) => options.editorCallback(e.value)}
      placeholder="Select Gender"
      className="w-full"
    />
  );

  useEffect(() => {
    const fetchPairBulls = async () => {
      try {
        const data = await desiriBullService.getAllBullsForPairing();
        setPairBullOptions(data);
      } catch (err) {
        console.error("Failed to load bulls for pairing", err);
      }
    };

    fetchPairBulls();
  }, []);

// const pairBullEditor = (options: ColumnEditorOptions) => (
//   <Dropdown
//     value={options.value ?? null}              // current value is the name
//     options={pairBullOptions.map(p => p.label)} // array of strings for name
//     onChange={(e) => options.editorCallback?.(e.value)} // update pairBullName
//     placeholder="Select Pair Bull"
//     filter
//     showClear
//     className="w-full"
//   />
// );

// interface PairBullDropdownProps {
//   options: ColumnEditorOptions;
//   pairBullOptions: { label: string; value: number }[];
// }

// const PairBullDropdown: React.FC<PairBullDropdownProps> = ({ options, pairBullOptions }) => {
//   // Extract all labels from pairBullOptions
//   const optionLabels = pairBullOptions.map(p => p.label);

//   return (
//     <Dropdown
//       value={options.value ?? null}       // current value (string)
//       options={optionLabels}              // array of strings
//       onChange={(e) => options.editorCallback?.(e.value)} // update row data
//       placeholder="Select Pair Bull"
//       filter
//       showClear
//       className="w-full"
//     />
//   );
// };

const pairBullEditor = (options: ColumnEditorOptions) => (
  <Dropdown
    value={options.value ?? null}        // current ID
    options={pairBullOptions}            // array of { label, value }
    onChange={(e) => options.editorCallback?.(e.value)} // update rowData.pairOFthebull
    placeholder="Select Pair Bull"
    filter
    showClear
    className="w-full"
  />
);
  return (
    <div className="col-12">
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="card p-2">
        <DataTable
          value={bullData}
          editMode="row"
          dataKey="id"
          onRowEditComplete={onRowEditComplete}
          paginator
          rows={5}
          stripedRows
          rowsPerPageOptions={[5, 10, 25, 50]}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
           scrollHeight=""  
           scrollable 
        >
          <Column
            field="bullNumber"
            header="Bull Number"
            editor={textEditor}
            className="w-full"
            sortable
          />

          <Column
            field="name"
            header="Name"
            editor={textEditor}
            className="w-full"
            sortable
          />
        

<Column
  field="dob"
  header="DOB"
  body={(rowData) => {
    if (!rowData.dob) return "";
    const date = new Date(rowData.dob);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2); 
    return `${day}/${month}/${year}`;
  }}
  editor={dobEditor}
  sortable
  style={{ width: "25%" }}
/>

          <Column field="age" header="Age" sortable editor={textEditor} />
          <Column
            field="gender"
            header="Gender"
            editor={genderEditor}
            sortable
            style={{ width: "25%" }}
          />
          {/* <Column field="gender" header="Gender" sortable editor={textEditor} /> */}
          <Column field="breed" header="Breed" sortable editor={textEditor} />
          <Column field="color" header="Color" sortable editor={textEditor} />
          <Column
            field="weightKG"
            header="Weight (KG)"
            sortable
            editor={textEditor}
          />
          <Column
            field="isForBreeding"
            header="For Breeding"
            editor={isForBreedingEditor}
            body={(row) => (row.isForBreeding ? "Yes" : "No")}
            sortable
          />
          {/* <Column field="isForBreeding" header="For Breeding" body={(row) => (row.isForBreeding ? "Yes" : "No")} sortable editor={textEditor}/> */}
         
{/* <Column
  field="pairBullName"
  header="Paired With"
  body={(rowData) => {
    const selected = pairBullOptions.find(p => p.value === rowData.pairBullName);
    return selected ? selected.label : "";
  }}
  editor={(options: ColumnEditorOptions) => (
    <Dropdown
      value={options.value ?? null} // numeric ID
      options={pairBullOptions}     // { label, value }
      onChange={(e) => options.editorCallback?.(e.value)}
      placeholder="Select Pair Bull"
      filter
      showClear
      className="w-full"
    />
  )}
  sortable
/> */}

   
<Column
  field="pairOFthebull"                  // stores the ID
  header="Paired With"
  body={(rowData) => rowData.pairBullName || ""} // display the name from API
  editor={pairBullEditor}
  sortable
/>
          <Column
            field="posID" 
            header="Manufacturing Unit"
            body={(rowData) => {
              const mu = MUOptions.find((m) => m.value === rowData.posID);
              return mu ? mu.label : "";
            }}
            editor={muEditor}
            sortable
             
            style={{ width: "25%" }}
          />
          <Column
            rowEditor
            header="Action"
            headerStyle={{ textAlign: "right" }}
            frozen
            alignFrozen="right"
          />
          <Column
            header="Delete"
            body={(rowData) => (
              <Button
                icon="pi pi-trash"
                onClick={() => confirmDelete(rowData)}
                className="p-button-rounded p-button-danger p-button-text"
              />
            )}
            style={{ width: "8%" }}
             frozen
  alignFrozen="right"
          />
        </DataTable>
      </div>
    </div>
  );
};

export default DesiriBullsTable;
